import { ParticipantResourceSelection, ResourceOption } from '@kouto/types';
import { ParticipantSelections } from 'utils/resourceSelections/resourceSelections';

export interface VariantRow {
  available: number;
  key: string;
  label: string;
  valueIds: string[];
}

/** How many of each variant a price tier takes: tier name, then variant row key. */
export type VariantCounts = Record<string, Record<string, number>>;

/** One row per kind of unit that exists (Electric, Large), in the order the attributes list their values. */
export const deriveVariantRows = (option: ResourceOption): VariantRow[] => {
  const valueOf = (
    valueIds: string[],
    attribute: ResourceOption['attributes'][number],
  ) =>
    attribute.values.find((value) =>
      valueIds.includes(value.resourceAttributeValueId),
    );

  return option.combinations
    .filter((combination) => combination.valueIds.length > 0)
    .map((combination) => {
      const valueIds = [...combination.valueIds];
      const values = option.attributes.map((attribute) =>
        valueOf(valueIds, attribute),
      );
      return {
        orders: values.map((value) => value?.sortOrder ?? 0),
        row: {
          available: combination.available,
          key: [...valueIds].sort().join('|'),
          label: values.flatMap((value) => value?.label ?? []).join(' · '),
          valueIds,
        },
      };
    })
    .sort((a, b) => {
      const differing = a.orders.findIndex(
        (order, index) => order !== b.orders[index],
      );
      return differing === -1
        ? a.row.label.localeCompare(b.row.label)
        : a.orders[differing] - b.orders[differing];
    })
    .map(({ row }) => row);
};

export const sumVariantCounts = (counts: Record<string, number> | undefined) =>
  Object.values(counts ?? {}).reduce((sum, count) => sum + count, 0);

/** The most a tier can take of one variant: what is free once the other tiers have taken theirs. */
export const deriveVariantMax = ({
  counts,
  row,
  tierName,
}: {
  counts: VariantCounts;
  row: VariantRow;
  tierName: string;
}) =>
  Math.max(
    0,
    row.available -
      Object.entries(counts).reduce(
        (sum, [name, tierCounts]) =>
          name === tierName ? sum : sum + (tierCounts[row.key] ?? 0),
        0,
      ),
  );

/** Drops counts the free units no longer cover, such as a variant that sold out meanwhile. */
export const normalizeVariantCounts = (
  counts: VariantCounts,
  tierNames: string[],
  rows: VariantRow[],
): VariantCounts => {
  const next: VariantCounts = {};
  tierNames.forEach((tierName) => {
    rows.forEach((row) => {
      const free =
        row.available -
        Object.values(next).reduce(
          (sum, other) => sum + (other[row.key] ?? 0),
          0,
        );
      const count = Math.max(
        0,
        Math.min(counts[tierName]?.[row.key] ?? 0, free),
      );
      if (count > 0) {
        next[tierName] = { ...next[tierName], [row.key]: count };
      }
    });
  });
  return next;
};

/** One list of picks per participant, tiers in order and each tier's variants in row order. */
export const toVariantSelections = ({
  counts,
  option,
  rows,
  tierNames,
}: {
  counts: VariantCounts;
  option: ResourceOption;
  rows: VariantRow[];
  tierNames: string[];
}): ParticipantSelections => {
  const attributeOf = new Map(
    option.attributes.flatMap((attribute) =>
      attribute.values.map(
        (value) =>
          [
            value.resourceAttributeValueId,
            attribute.resourceAttributeId,
          ] as const,
      ),
    ),
  );
  return tierNames.flatMap((tierName) =>
    rows.flatMap((row) =>
      Array.from({ length: counts[tierName]?.[row.key] ?? 0 }, () =>
        row.valueIds.map(
          (valueId): ParticipantResourceSelection => ({
            resourcePoolId: option.resourcePoolId,
            resourceAttributeId: attributeOf.get(valueId) ?? '',
            resourceAttributeValueId: valueId,
          }),
        ),
      ),
    ),
  );
};
